import { Default_Image, base64Image, dataLayer } from '@/constants/index';
import { useMegaMenu } from '@/hooks/homepage';
import { useProduct, useUpdateInfoGraphic } from '@/hooks/products';
import { IProduct } from '@/interfaces/product';
import useHeaderStore from '@/store/header';
import { numberFormat } from '@/utils/helper';
import { useQueryClient } from '@tanstack/react-query';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { ProductColorVariants } from './ProductColorVariants';

interface IProductCard {
  className?: string;
  name: string;
  category?: string;
  url: string;
  imageUrl: string;
  imageAlt?: string;
  imageAspectRatio?: string;
  options?: string;
  topRightIcon?: any;
  topRightIconClassName?: string;
  seName?: any;
  oldPrice?: number;
  price: number;
  isCategoryProduct?: boolean;
  colorVariants?: boolean;
  largeSize?: boolean;
  ColorVariants?: [
    {
      ColorName: string;
      ColorHex: string;
      FeaturedImageUrl: string;
      IsOwn: boolean;
    },
  ];
  shortDescription?: string;
  isCategoryVisible?: boolean;
}

const ProductCard = ({
  className = '',
  name,
  url,
  imageUrl,
  imageAlt,
  imageAspectRatio,
  category,
  options,
  oldPrice,
  price,
  seName,
  isCategoryProduct,
  colorVariants,
  largeSize,
  ColorVariants,
  topRightIconClassName,
  topRightIcon,
  shortDescription,
  isCategoryVisible,
}: Partial<IProduct> & IProductCard) => {
  const [initProductDetail, setInitProductDetail] = useState(false);
  const { data: megaMenuData } = useMegaMenu();
  const [productCardSeName, setProductCardSeName] = useState<string>(seName);

  const { cartPanel } = useHeaderStore(({ cartPanel }) => ({
    cartPanel,
  }));
  const { openQuickView, openQuickViewLoading, setQuickViewProduct, setQuickViewSeName, quickViewSeName } = useHeaderStore();
  const { data: productDetail, isFetching } = useProduct(productCardSeName, initProductDetail);
  const [checked, setChecked] = useState('');

  const [selectedImage, setSelectedImage] = useState('');
  const [availableColor, setAvailableColor] = useState(ColorVariants);
  const { mutate: mutateInfoGraphic } = useUpdateInfoGraphic(seName, true);
  const [loaded, setLoaded] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (colorVariants) {
      availableColor?.map((color) => {
        if (color.IsOwn) {
          setSelectedImage(color.FeaturedImageUrl);
          setChecked(color.ColorHex);
        }
      });
    }
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (colorVariants) {
      availableColor?.map((color) => {
        if (checked == color.ColorHex) {
          setSelectedImage(color.FeaturedImageUrl);
          setChecked(color.ColorHex);
        }
      });
    }
  }, [checked]);

  useEffect(() => {
    if (isFetching) {
      openQuickViewLoading(true);
    } else {
      openQuickViewLoading(false);
    }

    if (productDetail?.Id) {
      setQuickViewProduct(productDetail);
    }
  }, [productDetail, isFetching]);

  useEffect(() => {
    setProductCardSeName(quickViewSeName);
  }, [quickViewSeName]);

  useEffect(() => {
    cartPanel ? openQuickView(false) : null;
  }, [cartPanel]);

  function getHeight() {
    if (typeof document !== 'undefined') {
      const el = document.getElementById(seName + 'product-card');
      const divHeight = Number(el?.scrollHeight || 1);
      return divHeight;
    } else {
      return 1;
    }
  }
  {
    /* 3 lines 12 base font-size 1.25 rem 1.4 line-height */
  }
  const maxHeightOfDescription = Math.ceil(3 * 12 * 1.25 * 1.4);
  return (
    <>
      <div
        onMouseEnter={() => {
          setInitProductDetail(true);
          openQuickViewLoading(true);
        }}
        className={`product-card flex h-full flex-col justify-between  ${className}`} // ${isCategoryProduct ? 'mx-5 first:ml-1 md:mr-5' : ''} will add if needed
      >
        <div
          className={`image-container relative flex w-full  overflow-hidden ${imageAspectRatio ? imageAspectRatio : 'aspect-square'} ${
            isCategoryProduct ? 'bg-[#ECEFF1]' : ''
          }`}
        >
          <div className="group flex w-full">
            <Link prefetch={false} href={url} className="absolute inset-0">
              <Image
                src={selectedImage !== '' ? selectedImage : `${imageUrl ? imageUrl : Default_Image}`}
                alt={imageAlt || ''}
                fill
                //quality={60}
                className="transition-transform lg:group-hover:scale-125"
                placeholder="blur"
                blurDataURL={base64Image}
                unoptimized={true}
              />
            </Link>
            {megaMenuData?.IsQuickViewEnabled && (
              <button
                onClick={() => {
                  setProductCardSeName(seName);
                  mutateInfoGraphic(seName);
                  openQuickView(true);
                  queryClient.invalidateQueries([seName]);
                  let obj: { [key: string]: string } = {};
                  if (productDetail?.CategoryName) {
                    let splittedCategories = productDetail?.CategoryName.split(',');
                    splittedCategories.forEach((category, categoryIndex) => {
                      obj['item_category' + (categoryIndex ? categoryIndex : '')] = category;
                    });
                  }
                  if (!productDetail?.IsGiftVoucher && productDetail?.Gender) {
                    obj['gender'] = productDetail?.Gender;
                  }
                  dataLayer.push({ ecommerce: null });
                  dataLayer.push({
                    event: 'view_item',
                    ecommerce: {
                      currency: 'BDT',
                      value: productDetail?.ProductPrice.Price,
                      items: [
                        {
                          item_id: productDetail?.SeName,
                          item_name: productDetail?.Name,
                          item_sku: productDetail?.Sku,
                          item_brand: productDetail?.ProductBrand?.Name,
                          price: productDetail?.ProductPrice.Price,
                          quantity: productDetail?.StockQuantity,
                          item_variant: productDetail?.Variations?.find((x) => x.IsSelected)?.VariantSku,
                          size: productDetail?.Variations?.map((variation) => variation?.ProductAttributeValue),
                          color: productDetail?.AvailableColors?.map((color) => color?.ColorName),
                          ...obj,
                        },
                      ],
                    },
                  });
                }}
                className="quick-view-button absolute  top-[80%] left-[50%] h-fit min-w-[20%] translate-x-[-50%] scale-0 items-center  bg-white py-2 opacity-0 group-hover:scale-100  group-hover:opacity-100  max-lg:hidden "
              >
                Quick View
              </button>
            )}
          </div>
          {topRightIcon && (
            <div
              className={`z-9 absolute flex h-8  w-8 items-center justify-center ${
                topRightIconClassName ? `${topRightIconClassName}` : 'top-2 right-2'
              }`}
            >
              {topRightIcon}
            </div>
          )}
        </div>
        <Link prefetch={false} href={url} className={`group block px-1.5 pt-4`}>
          {isCategoryVisible !== false && category && (
            <p className={`mb-1 text-gray-400 ${largeSize ? 'text-xl md:text-2xl lg:text-3xl' : 'text-base sm:text-lg'}`}>{category}</p>
          )}
          <p
            className={`}  whitespace-normal line-clamp-3 group-hover:underline ${
              largeSize ? 'text-xl md:text-2xl lg:text-3xl' : 'text-base sm:text-lg'
            } `}
          >
            {name}
          </p>
          {price >= 0 && (
            <>
              <p className="mt-1 flex items-center space-x-4">
                {Number(oldPrice) > Number(price) && (
                  <span className={`text-gray-300 line-through ${largeSize ? 'text-xl md:text-2xl lg:text-3xl' : 'text-lg  sm:text-lg'}`}>
                    <span className="currency">৳</span>
                    {numberFormat(oldPrice)}
                  </span>
                )}
                <span className={`font-bold ${largeSize ? 'text-xl md:text-2xl lg:text-3xl' : 'text-xl sm:text-lg'}`}>
                  <span className="currency">৳</span>
                  {numberFormat(price)}
                </span>
              </p>
              {/* {oldPrice && Number(oldPrice) > Number(price) && (
                <div className={`font-semibold text-red-500 ${largeSize ? 'text-xl md:text-2xl lg:text-3xl' : 'text-lg  sm:text-lg'}`}>
                  {parseFloat((((+oldPrice - price) / +oldPrice) * 100).toFixed(2))}% off
                </div>
              )} */}
            </>
          )}
        </Link>
        {largeSize && shortDescription && (
          <div className="relative">
            <div
              className="text-cutoff relative overflow-hidden px-1.5 text-xl opacity-50"
              id={seName + 'product-card'}
              dangerouslySetInnerHTML={{ __html: shortDescription }}
            ></div>

            {loaded && getHeight() > maxHeightOfDescription && (
              <Link
                prefetch={false}
                href={`/product/${seName}`}
                className="absolute bottom-0 right-0 block w-fit bg-white pt-1 pr-8 pl-16 text-right leading-7 text-gray-500 underline"
                style={{ background: 'linear-gradient(90deg, rgb(255 255 255 / 40%) 0%, #fff 25%)' }}
              >
                Continue Reading
              </Link>
            )}
          </div>
        )}
        {colorVariants && (
          <div className="flex flex-row gap-x-3 px-1.5">
            {availableColor?.map((color, index) => (
              <ProductColorVariants
                key={color.ColorHex + index}
                name={color.ColorName}
                value={color.ColorHex}
                className=""
                checked={checked}
                setChecked={setChecked}
                largeSize={largeSize}
              />
            ))}
          </div>
        )}
      </div>

      <style jsx>{`
        .quick-view-button {
          box-shadow: 0 0 0 5000px rgb(0 0 0 / 50%);
          line-height: 1.17;
        }
      `}</style>
    </>
  );
};

export default ProductCard;
